import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowRight } from 'lucide-react';

export const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'GEO/AIO Growth',
      tagline: 'For brands starting their AI visibility journey',
      price: { monthly: 3500, annual: 3150 },
      features: [
        'Optimization of 2-3 key pages per month',
        'Basic schema and structured data',
        '1-2 authority building actions',
        'Monthly AI visibility report',
        'Citation and mention tracking',
        'Monthly strategy call',
        'Email support (48h response)'
      ],
      cta: 'Start with Free Audit',
      popular: false
    },
    {
      name: 'GEO/AIO Scale',
      tagline: 'Become the leading answer in your category',
      price: { monthly: 6500, annual: 5850 },
      features: [
        'Everything in Growth, plus:',
        'Optimization of 5-7 key pages per month',
        'Advanced content assets for AI citation',
        'Aggressive PR and authority campaigns',
        'Competitive AI visibility benchmarking',
        'Bi-weekly strategy calls',
        'Priority support (24h response)',
        'Quarterly roadmap reviews'
      ],
      cta: 'Start with Free Audit',
      popular: true
    },
    {
      name: 'GEO/AIO Enterprise',
      tagline: 'For teams with aggressive AI search goals',
      price: null,
      features: [
        'Everything in Scale, plus:',
        'Multi-brand or multi-market support',
        'Custom GEO/AIO roadmap',
        'AI Ads management (ChatGPT & more)',
        'Custom AI agents & automations',
        'Dedicated strategist',
        'Slack/email access',
        'Custom reporting and analytics'
      ],
      cta: 'Talk to Us',
      popular: false
    }
  ];

  const allPlansInclude = [
    'GEO/AIO strategy tailored to your market',
    'Ongoing AI visibility tracking and reporting',
    'Access to our team for questions and updates',
    'Regular updates on AI search trends'
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Transparent <span className="text-gradient">GEO/AIO Plans</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Start with a free audit, then choose a retainer that fits your ambition. Every plan is focused on increasing your AI visibility and turning it into revenue.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center space-x-4 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <Badge className="ml-2 bg-success text-success-foreground">Save 10%</Badge>
            </button>
          </div>
        </div>
      </section>

      {/* All Plans Include */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-display font-semibold text-center mb-6">All plans include:</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {allPlansInclude.map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative flex flex-col ${
                  plan.popular
                    ? 'border-primary shadow-primary scale-105 md:scale-110'
                    : 'border-border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="gradient-primary text-primary-foreground px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="font-display text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-base">{plan.tagline}</CardDescription>
                  
                  <div className="pt-6">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline">
                          <span className="text-4xl font-display font-bold">
                            ${plan.price[billingCycle].toLocaleString()}
                          </span>
                          <span className="text-muted-foreground ml-2">/month</span>
                        </div>
                        {billingCycle === 'annual' && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Billed annually (${(plan.price.annual * 12).toLocaleString()}/year)
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-3xl font-display font-bold">Custom</div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col">
                  <div className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className={feature.startsWith('Everything') ? 'font-semibold' : ''}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <Link to={plan.price ? '/free-audit' : '/contact'} className="mt-auto">
                    <Button
                      className={`w-full ${
                        plan.popular
                          ? 'gradient-primary text-primary-foreground shadow-primary'
                          : ''
                      }`}
                      variant={plan.popular ? 'default' : 'outline'}
                      size="lg"
                    >
                      {plan.cta}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Automations */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Need Custom Programmatic SEO/GEO Automations?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            We design and build automations for content and programmatic SEO pages around trending GEO-aligned keywords, so your brand shows up in more AI answers. Pricing depends on complexity and integrations.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="outline">
              Discuss Custom Automations
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Not Sure Which Plan Is Right?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Start with a free GEO/AIO audit and we'll recommend the best fit based on your goals and current AI visibility.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free GEO/AIO Audit <span className="font-semibold">(Valued at $2,000+)</span>
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-3">
            This audit is typically valued at <span className="font-semibold">$2,000+</span>—it&apos;s what many agencies charge as a paid engagement.
          </p>
        </div>
      </section>
    </div>
  );
};
